import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || 'secretary@krinapanchalphysio.com';

// Single axios instance with credentials so the httpOnly cookie set on
// /auth/login is sent on every subsequent admin request. This keeps the JWT
// out of localStorage (XSS-safe).
const authAxios = axios.create({ baseURL: API, withCredentials: true });

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  // null = checking, false = logged out, object = logged in
  const [admin, setAdmin] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await authAxios.get('/auth/me');
      setAdmin(data);
    } catch (err) {
      // 401 here is the normal "no valid session" path — not an error.
      if (err.response?.status !== 401) {
        console.error('Auth check failed:', err);
      }
      setAdmin(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (password) => {
    const { data } = await authAxios.post('/auth/login', {
      email: ADMIN_EMAIL,
      password,
    });
    setAdmin(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authAxios.post('/auth/logout');
    } catch (err) {
      console.error('Logout request failed:', err);
    }
    setAdmin(false);
  }, []);

  const value = useMemo(
    () => ({ admin, login, logout, authAxios }),
    [admin, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
